import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'

/**
 * Full-screen image dialog for portfolio assets (master prompt §5.3:
 * "modal/dialog closes with Escape", "correct focus management", "no
 * background scroll leakage"). Native <dialog> gives us focus trapping and
 * Escape-to-close for free; we still wire scroll-lock ourselves since
 * <dialog> doesn't do that.
 */
export function Lightbox({
  image,
  alt,
  onClose,
}: {
  image: string | null
  alt: string
  onClose: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = dialogRef.current
    if (!el) return
    if (image && !el.open) {
      el.showModal()
      document.body.style.overflow = 'hidden'
    } else if (!image && el.open) {
      el.close()
      document.body.style.overflow = ''
    }
  }, [image])

  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose()
      }}
      className="m-auto max-h-[90vh] max-w-[90vw] overflow-visible rounded-lg bg-transparent p-0 backdrop:bg-background/90 backdrop:backdrop-blur-sm"
    >
      {image && (
        <div className="relative">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute -top-4 -right-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-surface text-foreground transition-colors hover:text-accent-fire"
          >
            <X size={18} />
          </button>
          <img src={image} alt={alt} className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain" />
        </div>
      )}
    </dialog>
  )
}
